import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, utils } from '../api/authAPI';
import { STORAGE_KEYS, SESSION_MAX_MS } from '../../../config/storageKeys';
import { clearPasswordChangeSignIn } from '../passwordChangeSignIn';
import {
  clearPaperProgressCaches,
  purgeLegacyPaperProgressCaches,
} from '../../quiz-generation/utils/paperProgressCache';
import {
  clearInterviewProgress,
  purgeLegacyInterviewProgress,
} from '../../interview/interviewProgressStorage';
import { withAppBasename } from '../../../config/appBase';
import './AuthContext.css';

// Create Auth Context
const AuthContext = createContext();

const readJwtIatMs = (token) => {
  if (!token || typeof token !== 'string') return 0;
  try {
    const part = token.split('.')[1];
    if (!part) return 0;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(padded));
    return payload.iat ? payload.iat * 1000 : 0;
  } catch {
    return 0;
  }
};

/** Persist absolute login start so client + server both enforce a fixed 24h session. */
const markSessionStarted = (rememberMe = true, startedAtMs = Date.now()) => {
  const storage = rememberMe ? localStorage : sessionStorage;
  const other = rememberMe ? sessionStorage : localStorage;
  storage.setItem(STORAGE_KEYS.SESSION_STARTED_AT, String(startedAtMs));
  other.removeItem(STORAGE_KEYS.SESSION_STARTED_AT);
};

const getSessionStartedAtMs = () => {
  const raw =
    localStorage.getItem(STORAGE_KEYS.SESSION_STARTED_AT) ||
    sessionStorage.getItem(STORAGE_KEYS.SESSION_STARTED_AT);
  const parsed = raw ? Number(raw) : 0;
  if (parsed > 0) return parsed;

  // Migrate older sessions: use access-token iat as the absolute start.
  const accessToken =
    localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
    sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const iatMs = readJwtIatMs(accessToken);
  if (iatMs > 0) {
    const rememberMe =
      localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true' ||
      Boolean(localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN));
    markSessionStarted(rememberMe, iatMs);
    return iatMs;
  }
  return 0;
};

const isAbsoluteSessionExpired = () => {
  const startedAt = getSessionStartedAtMs();
  if (!startedAt) return false;
  return Date.now() - startedAt >= SESSION_MAX_MS;
};

/**
 * The `access` object as the app stores it. `has_paid` and `owned` are the
 * server's account model; both are optional (an older server or stored
 * session has neither, and `owned` is null with `owned_error` when its read
 * failed), so a malformed value is dropped rather than trusted. Everything
 * else passes through untouched.
 */
export const normaliseAccess = (access) => {
  if (!access || typeof access !== 'object' || Array.isArray(access)) return undefined;
  const { has_paid: hasPaid, owned, ...rest } = access;
  const next = { ...rest };
  if (typeof hasPaid === 'boolean') next.has_paid = hasPaid;
  if (owned === null || (owned && typeof owned === 'object' && !Array.isArray(owned))) next.owned = owned;
  return next;
};

/**
 * The user object a sign-in response (password login, magic link, LinkedIn,
 * Google) is stored as. `access` is the server's account model
 * ({ audience, plan, org, can_purchase, ... }).
 *
 * checkAuthStatus does not use this: its /me mapping differs on purpose. It
 * has no role default, sets must_change_password only for an exact `true`,
 * and falls back to the stored session's plan and access.
 */
export const mapAuthUser = (userInfo) => ({
  id: userInfo.id,
  email: userInfo.email,
  name: userInfo.name,
  role: userInfo.role || 'student',
  primary_subject: userInfo.primary_subject,
  student_level: userInfo.student_level,
  first_name: userInfo.first_name,
  last_name: userInfo.last_name,
  active_plan: userInfo.active_plan,
  is_org_admin: Boolean(userInfo.is_org_admin),
  must_change_password: Boolean(userInfo.must_change_password),
  access: normaliseAccess(userInfo.access),
});

/**
 * Merges GET /api/auth/me over a freshly mapped sign-in user. Plan and
 * access keep the sign-in value when /me has none.
 */
export const mergeMeProfile = (userData, profile) => ({
  ...userData,
  first_name: profile.first_name,
  last_name: profile.last_name,
  bio: profile.bio,
  avatar_url: profile.avatar_url,
  created_at: profile.created_at,
  primary_subject: profile.primary_subject,
  student_level: profile.student_level,
  active_plan: profile.active_plan || userData.active_plan,
  is_org_admin: Boolean(profile.is_org_admin),
  must_change_password: Boolean(profile.must_change_password),
  access: normaliseAccess(profile.access) || userData.access,
});

// Helper to synchronously read initial state from storage
const getInitialAuthState = () => {
  try {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || 
                        sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const userDataRaw = localStorage.getItem(STORAGE_KEYS.USER_DATA) || 
                        sessionStorage.getItem(STORAGE_KEYS.USER_DATA) ||
                        localStorage.getItem('pathbuddy_user') ||
                        localStorage.getItem('user');

    let storedUser = null;
    if (userDataRaw) {
      try {
        storedUser = typeof userDataRaw === 'string' && userDataRaw.startsWith('{')
          ? JSON.parse(userDataRaw)
          : { email: userDataRaw };
      } catch (e) {
        storedUser = null;
      }
    }

    if (accessToken && storedUser) {
      return {
        user: storedUser,
        isAuthenticated: true,
        isLoading: true
      };
    }

    return {
      user: storedUser,
      isAuthenticated: false,
      isLoading: !!accessToken
    };
  } catch (e) {
    return { user: null, isAuthenticated: false, isLoading: false };
  }
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const initial = getInitialAuthState();
  const [user, setUser] = useState(initial.user);
  const [isAuthenticated, setIsAuthenticated] = useState(initial.isAuthenticated);
  const [isLoading, setIsLoading] = useState(initial.isLoading);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Public pages mounted right now (Edu marketing, the company home). See
  // usePublicPage.
  const [publicPages, setPublicPages] = useState(0);
  const registerPublicPage = useCallback(() => {
    setPublicPages((n) => n + 1);
    return () => setPublicPages((n) => Math.max(0, n - 1));
  }, []);

  // The paper workspaces and interviews once stored progress under keys
  // every account on the browser shared. They are never read now; delete
  // them on first load.
  useEffect(() => {
    purgeLegacyPaperProgressCaches();
    purgeLegacyInterviewProgress();
  }, []);

  // A sign-in as another account than the one this browser last held drops
  // every account's paper progress, so B never finds A's copies in storage
  // when A did not log out. No cached account counts as another: a closed
  // session tab forgets its user but not the progress kept for it.
  const forgetOtherAccountsProgress = (nextUserId) => {
    let cachedId = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.USER_DATA)
        || sessionStorage.getItem(STORAGE_KEYS.USER_DATA)
        || localStorage.getItem('pathbuddy_user')
        || localStorage.getItem('user');
      cachedId = raw ? JSON.parse(raw)?.id ?? null : null;
    } catch {
      cachedId = null;
    }
    if (cachedId == null || nextUserId == null || String(cachedId) !== String(nextUserId)) {
      clearPaperProgressCaches();
      clearInterviewProgress();
    }
    // The caller then writes the new account to one storage only. Drop the
    // previous account's copy from both, so a reload never starts as it.
    try {
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
    } catch { /* blocked storage: the write that follows fails the same way */ }
  };

  // Token management
  const getAccessToken = () => {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || 
           sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  };

  const getRefreshToken = () => {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || 
           sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  };

  const storeTokens = (accessToken, refreshToken, rememberMe = false) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    const other = rememberMe ? sessionStorage : localStorage;

    storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    // Prevent stale JWTs in the other storage from shadowing the active pair
    other.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    other.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (rememberMe) {
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    }
  };

  const clearTokens = () => {
    // Clear from both storages
    [localStorage, sessionStorage].forEach(storage => {
      Object.values(STORAGE_KEYS).forEach(key => {
        storage.removeItem(key);
      });
      storage.removeItem('pathbuddy_user');
      storage.removeItem('user');
      storage.removeItem('token');
      storage.removeItem('pathbuddy_access_token');
      storage.removeItem('pathbuddy_refresh_token');
      storage.removeItem('pathbuddy_session_started_at');
      storage.removeItem('ivori_lead_email');
      storage.removeItem('pending_checkout_tier');
      storage.removeItem('pending_checkout_exam');
      storage.removeItem('pending_checkout_package');
      storage.removeItem('pending_checkout_subject');
    });
    // Every account's paper and interview progress on this browser (shared
    // school computers).
    clearPaperProgressCaches();
    clearInterviewProgress();
  };

  useEffect(() => {
    const handleSessionExpired = () => {
      // APIService has already dropped the tokens; the paper progress goes
      // too. The interview progress stays: its sitting and draft keys are the
      // only copy of the run ids and unsent answers, and the open page keeps
      // writing them under the overlay. The cached user survives expiry, so a
      // sign-in as another account still clears them
      // (forgetOtherAccountsProgress); logout clears them too.
      clearPaperProgressCaches();
      setSessionExpired(true);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  // A session that ends on a public page ends quietly: the page simply shows
  // the signed-out state (the header offers "Sign in"). The overlay and its
  // forced trip to /login are for pages that need the account, where the
  // visitor may be mid-task. Storage is left as the expiry left it: whoever
  // set sessionExpired has already dropped the tokens (APIService, or
  // clearTokens on the 24-hour end), and an API-reported expiry keeps the
  // interview sitting and draft keys on purpose (see handleSessionExpired).
  const showExpiredOverlay = sessionExpired && publicPages === 0;
  useEffect(() => {
    if (!sessionExpired || publicPages === 0) return;
    setUser(null);
    setIsAuthenticated(false);
    setSessionExpired(false);
  }, [sessionExpired, publicPages]);

  // Lock background scroll while the session-expired overlay is up, so
  // iOS Safari's rubber-band/bounce scroll can't leak the page behind it
  // into view at the viewport edges.
  useEffect(() => {
    if (!showExpiredOverlay) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showExpiredOverlay]);

  // Quietly renew access tokens before they expire so idle users are not kicked
  // out early. Absolute session still ends at 24h (active or inactive).
  // Failures here must NOT show the session-expired popup — only real API 401+failed
  // refresh (via APIService.makeRequest) or the 24h absolute check should do that.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const endSessionIfAbsoluteExpired = () => {
      if (!isAbsoluteSessionExpired()) return false;
      clearTokens();
      setUser(null);
      setIsAuthenticated(false);
      setSessionExpired(true);
      return true;
    };

    const renewIfNeeded = () => {
      if (endSessionIfAbsoluteExpired()) return;
      authAPI.ensureAuthFresh().catch(() => {
        // Leave session intact on any proactive renew failure.
      });
    };

    renewIfNeeded();
    const intervalId = setInterval(renewIfNeeded, 10 * 60 * 1000);

    // visibilitychange covers most mobile background→foreground resumes.
    // pageshow (incl. bfcache), focus, and online cover iOS/Android/tablet gaps
    // where timers were frozen while the tab was suspended.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') renewIfNeeded();
    };
    const onPageShow = () => renewIfNeeded();
    const onFocus = () => renewIfNeeded();
    const onOnline = () => renewIfNeeded();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [isAuthenticated]);

  // Check for existing session on mount and listen to authChange / storage events
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const accessToken = getAccessToken();
        const userDataRaw = localStorage.getItem(STORAGE_KEYS.USER_DATA) || 
                            sessionStorage.getItem(STORAGE_KEYS.USER_DATA) ||
                            localStorage.getItem('pathbuddy_user') ||
                            localStorage.getItem('user');

        let storedUser = null;
        if (userDataRaw) {
          try {
            storedUser = typeof userDataRaw === 'string' && userDataRaw.startsWith('{')
              ? JSON.parse(userDataRaw)
              : { email: userDataRaw };
          } catch (e) {
            storedUser = null;
          }
        }
        
        // Email/profile JSON without a JWT is not a session (essay POST would 401).
        if (!accessToken) {
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // Absolute 24h session end — same on laptop, phone, and tablet.
        if (isAbsoluteSessionExpired()) {
          clearTokens();
          setUser(null);
          setIsAuthenticated(false);
          setSessionExpired(true);
          setIsLoading(false);
          return;
        }

        // Prefer /me before painting stored user so access is present before
        // apps page decides tier locks — avoids lock-tag flash.
        try {
          const result = await authAPI.getCurrentUser().catch(() => null);

          if (result) {
            const userProfile = {
              id: result.id,
              email: result.email,
              name: result.name,
              first_name: result.first_name,
              last_name: result.last_name,
              bio: result.bio,
              avatar_url: result.avatar_url,
              role: result.role,
              created_at: result.created_at,
              primary_subject: result.primary_subject,
              student_level: result.student_level,
              active_plan: result.active_plan || storedUser?.active_plan,
              is_org_admin: Boolean(result.is_org_admin),
              must_change_password: result.must_change_password === true,
              access: normaliseAccess(result.access) || storedUser?.access,
            };

            setUser((prev) => {
              if (
                prev &&
                prev.id === userProfile.id &&
                prev.email === userProfile.email &&
                prev.active_plan === userProfile.active_plan &&
                prev.primary_subject === userProfile.primary_subject &&
                prev.student_level === userProfile.student_level &&
                prev.first_name === userProfile.first_name &&
                prev.last_name === userProfile.last_name &&
                prev.avatar_url === userProfile.avatar_url &&
                prev.role === userProfile.role &&
                Boolean(prev.is_org_admin) === Boolean(userProfile.is_org_admin) &&
                Boolean(prev.must_change_password) === userProfile.must_change_password &&
                JSON.stringify(prev.access) === JSON.stringify(userProfile.access)
              ) {
                return prev;
              }
              return userProfile;
            });
            setIsAuthenticated(true);

            const storage = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true' ? localStorage : sessionStorage;
            storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userProfile));
            localStorage.setItem('pathbuddy_user', JSON.stringify(userProfile));
            localStorage.setItem('user', JSON.stringify(userProfile));
          } else if (storedUser) {
            setUser(storedUser);
            setIsAuthenticated(true);
          } else {
            setUser(null);
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.warn('Backend user verification warning (using stored session):', error);
          if (storedUser) {
            setUser(storedUser);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();

    const handleAuthEvent = () => {
      checkAuthStatus();
    };

    window.addEventListener('authChange', handleAuthEvent);
    window.addEventListener('storage', handleAuthEvent);

    return () => {
      window.removeEventListener('authChange', handleAuthEvent);
      window.removeEventListener('storage', handleAuthEvent);
    };
  }, []);

  // Register function
  const register = async (name, email, password, meta = {}, handoffToken = null) => {
    try {
      setIsActionLoading(true);

      const result = await authAPI.register(name, email, password, meta, handoffToken);

      if (result) {
        // Mark that this is a new signup that needs onboarding
        sessionStorage.setItem('needs_onboarding', 'true');

        // Capture any landing-handoff continuation info returned alongside
        // registration, before delegating to login() for the actual
        // token issuance below (login() itself is called with no handoff
        // token, since the claim — if any — already happened on this
        // register() call).
        const handoffContinuation = result && typeof result === 'object'
          ? {
              continuation_type: result.continuation_type,
              claimed_chat_session_id: result.claimed_chat_session_id,
              handoff_retry_token: result.handoff_retry_token,
            }
          : {};

        // After successful registration, automatically log in
        const loginResult = await login(email, password, true);
        return { ...loginResult, ...handoffContinuation };
      } else {
        return { success: false, error: 'Registration failed' };
      }
    } catch (error) {
      console.error('Register error:', error);
      const errMsg = utils.formatErrorMessage(error);
      return { success: false, error: errMsg };
    } finally {
      setIsActionLoading(false);
    }
  };

  // Login function
  const login = async (email, password, rememberMe = true, handoffToken = null) => {
    try {
      setIsActionLoading(true);

      const result = await authAPI.login(email, password, handoffToken);

      if (result) {
        const { access_token, refresh_token, expires_in, user: userInfo } = result;
        
        const userData = mapAuthUser(userInfo);
        forgetOtherAccountsProgress(userData.id);

        // Store tokens
        storeTokens(access_token, refresh_token, rememberMe);
        markSessionStarted(rememberMe);
        
        // Store user data
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
        
        // Store email for remember me functionality
        if (rememberMe) {
          localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email);
        }
        
        // Get full user profile from backend
        try {
          const profileResult = await authAPI.getCurrentUser();
          if (profileResult) {
            const fullUserData = mergeMeProfile(userData, profileResult);
            storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullUserData));
            setUser(fullUserData);
          } else {
            setUser(userData);
          }
        } catch (error) {
          console.warn('Could not fetch user profile:', error);
          setUser(userData);
        }
        
        setIsAuthenticated(true);

        return {
          success: true,
          user: userData,
          expiresIn: expires_in,
          continuation_type: result.continuation_type,
          claimed_chat_session_id: result.claimed_chat_session_id,
          handoff_retry_token: result.handoff_retry_token,
        };
      } else {
        return { success: false, error: 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: utils.formatErrorMessage(error) };
    } finally {
      setIsActionLoading(false);
    }
  };

  // Login with Magic Token function
  const loginWithMagicToken = async (token, rememberMe = true) => {
    try {
      setIsActionLoading(true);

      const result = await authAPI.magicLogin(token);

      if (result) {
        const { access_token, refresh_token, expires_in, user: userInfo } = result;
        
        const userData = mapAuthUser(userInfo);
        forgetOtherAccountsProgress(userData.id);

        // Store tokens
        storeTokens(access_token, refresh_token, rememberMe);
        markSessionStarted(rememberMe);
        
        // Store user data
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
        
        // Get full user profile from backend
        try {
          const profileResult = await authAPI.getCurrentUser();
          if (profileResult) {
            const fullUserData = mergeMeProfile(userData, profileResult);
            storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullUserData));
            setUser(fullUserData);
          } else {
            setUser(userData);
          }
        } catch (error) {
          console.warn('Could not fetch user profile:', error);
          setUser(userData);
        }
        
        setIsAuthenticated(true);

        return {
          success: true,
          user: userData,
          expiresIn: expires_in,
          continuation_type: result.continuation_type,
          claimed_chat_session_id: result.claimed_chat_session_id,
          handoff_retry_token: result.handoff_retry_token,
        };
      } else {
        return { success: false, error: 'Magic link login failed' };
      }
    } catch (error) {
      console.error('Magic link login error:', error);
      return { success: false, error: utils.formatErrorMessage(error) };
    } finally {
      setIsActionLoading(false);
    }
  };

  // Login with LinkedIn function
  const loginWithLinkedIn = async (code, redirectUri, rememberMe = true, handoffToken = null) => {
    try {
      setIsActionLoading(true);
      const result = await authAPI.linkedinLogin(code, redirectUri, handoffToken);
      
      if (result) {
        const { access_token, refresh_token, expires_in, user: userInfo } = result;
        const userData = mapAuthUser(userInfo);
        forgetOtherAccountsProgress(userData.id);

        storeTokens(access_token, refresh_token, rememberMe);
        markSessionStarted(rememberMe);
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));

        try {
          const profileResult = await authAPI.getCurrentUser();
          if (profileResult) {
            const fullUserData = mergeMeProfile(userData, profileResult);
            storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullUserData));
            setUser(fullUserData);
          } else {
            setUser(userData);
          }
        } catch (error) {
          console.warn('Could not fetch user profile details:', error);
          setUser(userData);
        }

        setIsAuthenticated(true);
        return {
          success: true,
          user: userData,
          expiresIn: expires_in,
          continuation_type: result.continuation_type,
          claimed_chat_session_id: result.claimed_chat_session_id,
          handoff_retry_token: result.handoff_retry_token,
        };
      } else {
        return { success: false, error: 'LinkedIn login failed.' };
      }
    } catch (error) {
      console.error('LinkedIn login error:', error);
      return { success: false, error: utils.formatErrorMessage(error) };
    } finally {
      setIsActionLoading(false);
    }
  };

  // Login with Google function
  const loginWithGoogle = async (code, redirectUri, rememberMe = true, handoffToken = null) => {
    try {
      setIsActionLoading(true);
      const result = await authAPI.googleLogin(code, redirectUri, handoffToken);
      
      if (result) {
        const { access_token, refresh_token, expires_in, user: userInfo } = result;
        const userData = mapAuthUser(userInfo);
        forgetOtherAccountsProgress(userData.id);

        storeTokens(access_token, refresh_token, rememberMe);
        markSessionStarted(rememberMe);
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));

        try {
          const profileResult = await authAPI.getCurrentUser();
          if (profileResult) {
            const fullUserData = mergeMeProfile(userData, profileResult);
            storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullUserData));
            setUser(fullUserData);
          } else {
            setUser(userData);
          }
        } catch (error) {
          console.warn('Could not fetch user profile details:', error);
          setUser(userData);
        }

        setIsAuthenticated(true);
        return {
          success: true,
          user: userData,
          expiresIn: expires_in,
          continuation_type: result.continuation_type,
          claimed_chat_session_id: result.claimed_chat_session_id,
          handoff_retry_token: result.handoff_retry_token,
        };
      } else {
        return { success: false, error: 'Google login failed.' };
      }
    } catch (error) {
      console.error('Google login error:', error);
      return { success: false, error: utils.formatErrorMessage(error) };
    } finally {
      setIsActionLoading(false);
    }
  };

  // Logout function
  const logout = async (clearRememberMe = false) => {
    try {
      setIsActionLoading(true);

      // Try to logout from backend
      try {
        await authAPI.logout();
      } catch (error) {
        console.warn('Backend logout failed:', error);
        // Continue with local logout even if backend fails
      }

      // Clear tokens and user data
      clearTokens();
      clearPasswordChangeSignIn();
      
      // Optionally clear remember me data
      if (clearRememberMe) {
        localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
        localStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
      }
      
      setUser(null);
      setIsAuthenticated(false);
      setSessionExpired(false);
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    } finally {
      setIsActionLoading(false);
    }
  };

  const acceptSessionFromPasswordChange = (result) => {
    forgetOtherAccountsProgress(result.user?.id);
    const rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    storeTokens(result.access_token, result.refresh_token, rememberMe);
    const newUser = { ...result.user, must_change_password: false };
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(newUser));
    setUser(newUser);
    setIsAuthenticated(true);
    return newUser;
  };

  // Update user data across all storage keys
  const updateUser = (updatedData) => {
    const newUserData = { ...user, ...updatedData };
    setUser(newUserData);
    
    // Sync across all browser storage keys
    const storage = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true' ? 
                    localStorage : sessionStorage;
    const userStr = JSON.stringify(newUserData);
    storage.setItem(STORAGE_KEYS.USER_DATA, userStr);
    localStorage.setItem('pathbuddy_user', userStr);
    localStorage.setItem('user', userStr);
    if (Object.prototype.hasOwnProperty.call(updatedData || {}, 'primary_subject')) {
      const subject = newUserData.primary_subject || 'undecided';
      localStorage.setItem('user_primary_subject', subject);
      sessionStorage.setItem('user_primary_subject', subject);
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    
    // Use first_name and last_name if available
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    
    // Fall back to name parsing
    if (user.name) {
      const names = user.name.split(' ');
      if (names.length >= 2) {
        return `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase();
      }
      return user.name.charAt(0).toUpperCase();
    }
    
    // Last resort: use email
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    
    return 'U';
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isActionLoading,
    sessionExpired,
    registerPublicPage,
    login,
    register,
    loginWithMagicToken,
    loginWithLinkedIn,
    loginWithGoogle,
    logout,
    acceptSessionFromPasswordChange,
    updateUser,
    getUserInitials,
    getAccessToken,
    getRefreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {showExpiredOverlay && (
        <div className="session-expired-notification">
          <div className="session-expired-content">
            <h3>Logged out due to inactivity</h3>
            <p>Your session ended after 24 hours. Please log in again.</p>
            <button
              onClick={() => {
                clearTokens();
                setUser(null);
                setIsAuthenticated(false);
                setSessionExpired(false);
                window.location.href = withAppBasename('/login');
              }}
            >
              Log in
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;